import { useParams } from 'react-router-dom';

import { OPS_COMPANY_MEMBER_HEADERS, ZERO } from '@/constants';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableContainer,
  Typography,
} from '@/ui';

import OpsMembersHeader from './OpsMembersHeader';
import OpsMembersList from './OpsMembersList';

import { useDownloadUsersCsv } from '@/features/companyDashboard/common/downloadUsersCsv';
import {
  COACH_IDS_KEY,
  COACHES_ASSIGNED_KEY,
  COHORT_TAG_IDS_KEY,
  END_DATE_STATUSES_KEY,
  INSIGHTS_STATUSES_KEY,
  MEMBER_STATUSES_KEY,
  MEMBERSHIP_KEY,
  MEMBERSHIP_OPTIONS,
  membershipParamValues,
  membershipSelection,
  SEAT_TYPES_KEY,
  useCompanyMembersParams,
} from '@/features/companyDashboard/common/members/useCompanyMembersParams';
import { useMembersFilterParams } from '@/features/companyDashboard/common/members/useMembersFilterParams';
import { useMembersTableState } from '@/features/companyDashboard/common/members/useMembersTableState';
import { useStickyColumnHeader } from '@/features/companyDashboard/common/members/useStickyColumnHeader';
import FilterDropdown from '@/features/membersTable/FilterDropdown';
import { useCompany, useCompanyMembers } from '@/hooks/useCompany';
import { useGetCompanyCohortTagsQuery } from '@/store/api/cohortTagApi';

// eslint-disable-next-line complexity -- one fallback per filter; splitting it would only scatter them.
const OpsMembers = () => {
  const {
    handleLimitChange,
    handlePageChange,
    handleSort,
    limit,
    page,
    sortDirection,
    sortField,
    validLimits,
  } = useMembersTableState();
  const { companyId = '' } = useParams();
  const { arrayFilter, sessionFilter, setArrayFilter, setSessionFilter } =
    useMembersFilterParams();
  const membersParams = useCompanyMembersParams('ops');
  const { companyData } = useCompany(companyId);
  const { canDownload, download, isDownloading } = useDownloadUsersCsv(
    'ops',
    companyData?.name,
  );
  const { bodyScrollerRef, stripRef } = useStickyColumnHeader();
  const { data: cohortTags = [] } = useGetCompanyCohortTagsQuery(companyId, {
    skip: !companyId,
  });

  const membershipFilter = membershipSelection(arrayFilter(MEMBERSHIP_KEY));
  const memberStatusFilter = membersParams.memberStatuses ?? [];
  const coachFilter = membersParams.coachIds ?? [];
  const endDateStatusFilter = membersParams.endDateStatuses ?? [];
  const coachesAssignedFilter = membersParams.coachesAssigned ?? [];
  const seatTypeFilter = membersParams.seatTypes ?? [];
  const cohortFilter = membersParams.cohortTagIds ?? [];
  const insightsStatusFilter = membersParams.insightsStatuses ?? [];

  const {
    data: queryData,
    isLoading,
    isFetching,
  } = useCompanyMembers(
    companyId,
    membersParams.companyMemberStatus,
    page,
    limit,
    sortField,
    sortDirection,
    undefined,
    {
      chemCall: membersParams.chemCall,
      coachIds: coachFilter,
      coachesAssigned: coachesAssignedFilter,
      cohortTagIds: cohortFilter,
      endDateStatuses: endDateStatusFilter,
      insightsStatuses: insightsStatusFilter,
      memberStatuses: memberStatusFilter,
      noUpcomingSessions: membersParams.noUpcomingSessions,
      scheduledSessions: membersParams.scheduledSessions,
      seatTypes: seatTypeFilter,
    },
  );
  // A skipped query keeps its last result; with no Membership selected, show nothing.
  const data = membersParams.companyMemberStatus ? queryData : undefined;
  const members = data?.items || [];
  const facets = data?.facets;

  const onDownloadCSV = async () => {
    try {
      await download();
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <div className="flex flex-1 flex-col pb-4">
      {/* Pinned to <main>, so it must stay outside every overflow wrapper. */}
      <div className="sticky top-0 z-10 flex flex-col bg-white pt-10">
        <div className="flex items-center justify-between">
          <Typography variant="h4">
            {isLoading ? 'Members' : `Members (${data?.total ?? 0})`}
          </Typography>
          <Button
            disabled={!canDownload || isDownloading}
            variant="outlined"
            onClick={onDownloadCSV}
          >
            {isDownloading ? 'Downloading...' : 'Download Users CSV'}
          </Button>
        </div>
        <div className="my-3 flex flex-wrap items-center justify-end gap-3">
          <span className="material-icons text-lg text-cement-400">
            filter_list
          </span>
          <FilterDropdown
            label="Membership"
            options={MEMBERSHIP_OPTIONS}
            selected={membershipFilter}
            onChange={(selected) =>
              setArrayFilter(MEMBERSHIP_KEY, membershipParamValues(selected))
            }
          />
          <FilterDropdown
            label="Status"
            options={facets?.memberStatuses ?? []}
            selected={memberStatusFilter}
            onChange={(selected) =>
              setArrayFilter(MEMBER_STATUSES_KEY, selected)
            }
          />
          <FilterDropdown
            label="Coach"
            options={facets?.coaches ?? []}
            selected={coachFilter}
            onChange={(selected) => setArrayFilter(COACH_IDS_KEY, selected)}
          />
          <FilterDropdown
            label="End Date Status"
            options={(facets?.endDateStatuses ?? []).map((status) => ({
              id: status,
              name: status,
            }))}
            selected={endDateStatusFilter}
            onChange={(selected) =>
              setArrayFilter(END_DATE_STATUSES_KEY, selected)
            }
          />
          <FilterDropdown
            label="Coaches Assigned"
            options={facets?.coachesAssigned ?? []}
            selected={coachesAssignedFilter}
            onChange={(selected) =>
              setArrayFilter(COACHES_ASSIGNED_KEY, selected)
            }
          />
          <FilterDropdown
            label="Seat Type"
            options={facets?.seatTypes ?? []}
            selected={seatTypeFilter}
            onChange={(selected) => setArrayFilter(SEAT_TYPES_KEY, selected)}
          />
          <FilterDropdown
            label="Sessions"
            options={facets?.sessionFilters ?? []}
            selected={sessionFilter}
            onChange={setSessionFilter}
          />
          <FilterDropdown
            label="Cohort"
            options={[{ id: 'none', name: 'None' }, ...cohortTags]}
            selected={cohortFilter}
            onChange={(selected) =>
              setArrayFilter(COHORT_TAG_IDS_KEY, selected)
            }
          />
          <FilterDropdown
            label="Insights"
            options={facets?.insightsStatuses ?? []}
            selected={insightsStatusFilter}
            onChange={(selected) =>
              setArrayFilter(INSIGHTS_STATUSES_KEY, selected)
            }
          />
        </div>
        <div ref={stripRef} className="overflow-hidden">
          <TableContainer className="min-w-[1600px] !rounded-b-none !border-b-0">
            <Table style={{ tableLayout: 'fixed' }}>
              <colgroup>
                {OPS_COMPANY_MEMBER_HEADERS.map((header) => (
                  <col key={header} />
                ))}
              </colgroup>
              <OpsMembersHeader
                sortDirection={sortDirection}
                sortField={sortField}
                onSort={handleSort}
              />
            </Table>
          </TableContainer>
        </div>
      </div>
      <div ref={bodyScrollerRef} className="w-full overflow-x-auto">
        <TableContainer className="min-w-[1600px] !rounded-t-none">
          <Table aria-label="ops company members table">
            <OpsMembersHeader
              sortDirection={sortDirection}
              sortField={sortField}
              collapsed
              onSort={handleSort}
            />
            <OpsMembersList
              companyId={companyId}
              isLoading={isLoading}
              isRefreshing={isFetching}
              members={members}
            />
          </Table>
        </TableContainer>
      </div>
      <div className="mt-2.5 flex items-center justify-between">
        <FormControl className="min-w-32">
          <InputLabel id="page-size-select-label">Per page</InputLabel>
          <Select
            id="page-size-select"
            label="Per page"
            labelId="page-size-select-label"
            value={limit}
            onChange={handleLimitChange}
          >
            {validLimits.map((pageLimit) => (
              <MenuItem key={pageLimit} value={pageLimit}>
                {pageLimit}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Pagination
          count={data?.pages ?? ZERO}
          page={page}
          onChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default OpsMembers;
